(() => {
  if (window.collectionTemplateMigrationLoaded) return;
  window.collectionTemplateMigrationLoaded = true;

  const gridPreferenceKey = 'usa-containers-collection-grid-columns';

  const getStoredGridPreference = () => {
    try {
      return localStorage.getItem(gridPreferenceKey);
    } catch (error) {
      return null;
    }
  };

  const setStoredGridPreference = (columns) => {
    try {
      localStorage.setItem(gridPreferenceKey, columns);
    } catch (error) {
      // Preference persistence is progressive enhancement only.
    }
  };

  const getHeaderOffset = () => {
    const stickyHeader = document.querySelector('sticky-header, .shopify-section-header-sticky, .shopify-section-header');
    if (!stickyHeader) return 0;

    const rect = stickyHeader.getBoundingClientRect();
    return Math.max(Math.round(rect.bottom), 0);
  };

  const updateStickyOffsets = () => {
    document.querySelectorAll('[data-collection-template-section]').forEach((section) => {
      section.style.setProperty('--collection-sticky-offset', `${getHeaderOffset()}px`);
    });
  };

  let shouldScrollOnNextGridUpdate = false;

  const markGridUpdateScroll = (event) => {
    if (
      event.target.closest('#FacetFiltersForm') ||
      event.target.closest('#FacetFiltersFormMobile') ||
      event.target.closest('facet-remove')
    ) {
      shouldScrollOnNextGridUpdate = true;
    }
  };

  const scrollToUpdatedResults = () => {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;

    const stickyControls = document.querySelector('[data-collection-sticky-controls]');
    const stickyControlHeight = stickyControls ? stickyControls.getBoundingClientRect().height : 0;
    const top =
      gridContainer.getBoundingClientRect().top +
      window.scrollY -
      getHeaderOffset() -
      Math.min(stickyControlHeight, 72) -
      12;

    window.scrollTo({
      top: Math.max(Math.round(top), 0),
      behavior: 'smooth',
    });
  };

  const setGridColumns = (section, columns) => {
    const normalizedColumns = columns === '1' ? '1' : '2';
    const grid = section.querySelector('[data-collection-grid]');
    const buttons = section.querySelectorAll('[data-collection-grid-view-button]');

    if (grid) {
      grid.dataset.mobileColumns = normalizedColumns;
    }

    buttons.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.collectionGridViewButton === normalizedColumns));
    });
  };

  const relocateActiveFacets = () => {
    document.querySelectorAll('[data-collection-sticky-controls]').forEach((controls) => {
      controls.querySelectorAll('.active-facets-mobile').forEach((activeFacets) => {
        const hasAppliedFilters = !!activeFacets.querySelector('.active-facets__button');
        activeFacets.classList.toggle('collection-active-facets--empty', !hasAppliedFilters);
      });

      const existingHost = controls.parentElement.querySelector('[data-collection-active-facets-host]');
      const hostedActiveFacets = existingHost?.querySelector('.active-facets-desktop');
      if (hostedActiveFacets) {
        const hasHostedFilters = !!hostedActiveFacets.querySelector('.active-facets__button');
        hostedActiveFacets.classList.toggle('collection-active-facets--empty', !hasHostedFilters);
      }

      const activeFacets = controls.querySelector('.active-facets-desktop');
      if (!activeFacets || !controls.parentElement) return;

      let host = existingHost;
      if (!host) {
        host = document.createElement('div');
        host.className = 'collection-active-facets-host page-width';
        host.dataset.collectionActiveFacetsHost = '';
        controls.insertAdjacentElement('afterend', host);
      }

      const hasAppliedFilters = !!activeFacets.querySelector('.active-facets__button');
      activeFacets.classList.toggle('collection-active-facets--empty', !hasAppliedFilters);
      host.appendChild(activeFacets);
    });
  };

  const initializeGridToggles = () => {
    document.querySelectorAll('[data-collection-template-section]').forEach((section) => {
      const storedColumns = getStoredGridPreference();
      if (storedColumns === '1' || storedColumns === '2') {
        setGridColumns(section, storedColumns);
      }
    });
  };

  const initializeVideoCarousel = (carousel) => {
    const track = carousel.querySelector('[data-collection-video-track]');
    const previousButton = carousel.querySelector('[data-collection-video-previous]');
    const nextButton = carousel.querySelector('[data-collection-video-next]');

    if (!track || !previousButton || !nextButton) return;

    const updateControls = () => {
      const maxScrollLeft = track.scrollWidth - track.clientWidth;
      previousButton.disabled = track.scrollLeft <= 1;
      nextButton.disabled = track.scrollLeft >= maxScrollLeft - 1;
    };

    const scrollByPage = (direction) => {
      track.scrollBy({
        left: direction * Math.max(track.clientWidth * 0.9, 280),
        behavior: 'smooth',
      });
    };

    previousButton.addEventListener('click', () => scrollByPage(-1));
    nextButton.addEventListener('click', () => scrollByPage(1));
    track.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls);
    updateControls();
  };

  document.addEventListener('click', (event) => {
    const quickAddButton = event.target.closest('.quick-add__submit[data-product-url][aria-haspopup="dialog"]');
    if (quickAddButton?.closest('[data-collection-template-section]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.href = quickAddButton.getAttribute('data-product-url');
      return;
    }

    const button = event.target.closest('[data-collection-grid-view-button]');
    if (!button) return;

    const section = button.closest('[data-collection-template-section]');
    if (!section) return;

    const columns = button.dataset.collectionGridViewButton === '1' ? '1' : '2';
    setStoredGridPreference(columns);
    setGridColumns(section, columns);
  });

  document.addEventListener('change', markGridUpdateScroll);
  document.addEventListener('input', markGridUpdateScroll);
  document.addEventListener('click', markGridUpdateScroll);

  document.addEventListener('DOMContentLoaded', () => {
    updateStickyOffsets();
    relocateActiveFacets();
    initializeGridToggles();
    document.querySelectorAll('[data-collection-video-carousel]').forEach(initializeVideoCarousel);
  });

  let stickyOffsetFrame = null;

  const requestStickyOffsetUpdate = () => {
    if (stickyOffsetFrame) return;

    stickyOffsetFrame = requestAnimationFrame(() => {
      stickyOffsetFrame = null;
      updateStickyOffsets();
    });
  };

  window.addEventListener('resize', requestStickyOffsetUpdate);
  window.addEventListener('scroll', requestStickyOffsetUpdate, { passive: true });

  const gridObserver = new MutationObserver(() => {
    relocateActiveFacets();
    initializeGridToggles();
    updateStickyOffsets();

    if (shouldScrollOnNextGridUpdate) {
      shouldScrollOnNextGridUpdate = false;
      requestAnimationFrame(scrollToUpdatedResults);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      gridObserver.observe(gridContainer, { childList: true, subtree: true });
    }

    const filters = document.getElementById('main-collection-filters');
    if (filters) {
      gridObserver.observe(filters, { childList: true, subtree: true });
    }
  });
})();
